import React, { useState, useEffect } from "react";
import {
  Box,
  Typography,
  TextField,
  InputAdornment,
  IconButton,
  Collapse,
  Drawer,
  Tooltip
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import LocalOfferOutlinedIcon from "@mui/icons-material/LocalOfferOutlined";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import FilterListIcon from '@mui/icons-material/FilterList';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';
import CancelIcon from '@mui/icons-material/Cancel';
import { useMediaQuery } from "@mui/material";
import styles from "./SolutionsPageLayout.module.css";
import ArrowBack from "@mui/icons-material/ArrowBack";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";


const SolutionsPageLayout = ({ 
  title, 
  data, 
  onItemClick,
  renderItemContent,
  getTagClassName
}) => {
  const [search, setSearch] = useState("");
  const [selectedTags, setSelectedTags] = useState([]);
  const [isFilterExpanded, setIsFilterExpanded] = useState(false);
  const [mobileFilterOpen, setMobileFilterOpen] = useState(false);
  const [expandedCards, setExpandedCards] = useState({});
  
  const isMobile = useMediaQuery('(max-width:600px)');
  const isTablet = useMediaQuery('(max-width:960px)');
  const navigate = useNavigate();

  const { t } = useTranslation();


  const allTags = Array.from(new Set(data.flatMap(item => item.tags)));

  const toggleTag = (tag) => {
    setSelectedTags(prev => 
      prev.includes(tag) 
        ? prev.filter(t => t !== tag)
        : [...prev, tag]
    );
  };

  const handleDeleteTag = (tagToDelete) => {
    setSelectedTags(prev => prev.filter(tag => tag !== tagToDelete));
  };

  const toggleCardExpansion = (index, e) => {
    e.stopPropagation();
    setExpandedCards(prev => ({
      ...prev,
      [index]: !prev[index]
    }));
  };
  
  useEffect(() => {
    if (!isMobile) {
      setMobileFilterOpen(false);
    }
  }, [isMobile]);

  const filteredData = data.filter(item =>
    (selectedTags.length === 0 || selectedTags.some(tag => item.tags.includes(tag))) &&
    (item.title.toLowerCase().includes(search.toLowerCase()) ||
    item.description.toLowerCase().includes(search.toLowerCase()) ||
    item.tags.some(tag => tag.toLowerCase().includes(search.toLowerCase())))
  );

  const handleFilterToggle = () => {
    if (isMobile) {
      setMobileFilterOpen(true);
    } else {
      setIsFilterExpanded(!isFilterExpanded);
    }
  };

  const renderFilterPanel = () => (
    <Box className={styles.filterPanel}>
      <Typography className={styles.filterPanelTitle}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <FilterListIcon sx={{ fontSize: 20 }} />
          Filter by Tags
        </Box>
      </Typography>
      <Box className={styles.filterTags}>
        {allTags.map((tag) => (
          <Box
            key={tag}
            onClick={() => toggleTag(tag)}
            className={`${styles.filterTag} ${styles[getTagClassName(tag)]} ${
              selectedTags.includes(tag) ? styles.selected : ""
            }`}
          >
            {tag}
          </Box>
        ))}
      </Box>
    </Box>
  );

  const renderCardContent = (item, index) => {
    const isExpanded = expandedCards[index];
    const threshold = isMobile ? 100 : 150;
    const shouldTruncate = item.description.length > threshold;

    let displayText = item.description;
    if (shouldTruncate && !isExpanded) {
      // Find the last space before the threshold to avoid breaking words
      const lastSpace = item.description.lastIndexOf(' ', threshold);
      displayText = item.description.substring(0, lastSpace > 0 ? lastSpace : threshold) + '...';
    }

    return (
      <Box>
        <Box className={styles.cardHeader}>
          <LightbulbOutlinedIcon className={styles.cardIcon} />
          <Typography className={styles.cardTitle}>
            {item.title}
          </Typography>
        </Box>
        
        <Typography 
          className={`${styles.cardDescription} ${isExpanded ? styles.expandedDescription : ''}`}
        >
          {displayText}
          {shouldTruncate && (
            <button
              className={styles.seeMore}
              onClick={(e) => toggleCardExpansion(index, e)}
            >
              {isExpanded ? 'Show less' : 'Read more'}
            </button>
          )}
        </Typography>

        <Box className={styles.tagsContainer}>
          {item.tags.map((tag) => (
            <Box
              key={tag}
              className={`${styles.tag} ${styles[getTagClassName(tag)]}`}
            >
              {tag}
            </Box>
          ))}
        </Box>
      </Box>
    );
  };

  return (
    <Box className={styles.pageWrapper}>
      <Box className={styles.container}>
        <button className={styles.backButton} onClick={() => navigate('/solutions')}>
          <ArrowBack />
          {t("solutions.navigation.backToSolutions")}
        </button>
        <Typography variant="h4" className={styles.title}>
          {title}
        </Typography>
        
        <Box className={styles.searchSection}>
          <Box className={styles.searchContainer}>
            <TextField
              fullWidth
              placeholder="Search ..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon sx={{ color: "#3b3b3b" }} />
                  </InputAdornment>
                ),
              }}
              sx={{
                '& .MuiOutlinedInput-root': {
                  backgroundColor: '#fff',
                  '& fieldset': { borderColor: 'rgba(0, 0, 0, 0.1)' },
                  '&:hover fieldset': { borderColor: 'rgba(0, 0, 0, 0.2)' },
                  '&.Mui-focused fieldset': { borderColor: '#0fe880' },
                },
                '& .MuiInputBase-input': { color: '#3b3b3b' }
              }}
            />
            <Tooltip title="Filter by tags" arrow placement="top">
              <IconButton
                className={`${styles.filterButton} ${isFilterExpanded || mobileFilterOpen ? styles.filterButtonActive : ''}`}
                onClick={handleFilterToggle}
                aria-label="Toggle filter panel"
                aria-expanded={isFilterExpanded || mobileFilterOpen}
              >
                {isFilterExpanded || mobileFilterOpen ? <LocalOfferIcon /> : <LocalOfferOutlinedIcon />}
              </IconButton>
            </Tooltip>
          </Box>
          
          {!isMobile && (
            <Collapse in={isFilterExpanded}>
              {renderFilterPanel()}
            </Collapse>
          )}

          <Drawer
            anchor="bottom"
            open={mobileFilterOpen}
            onClose={() => setMobileFilterOpen(false)}
            classes={{ paper: styles.filterDrawerMobile }}
          >
            {renderFilterPanel()}
          </Drawer>

          {/* Selected Filters Row */}
          {selectedTags.length > 0 && (
            <Box className={styles.selectedFilters}>
              {selectedTags.map((tag) => (
                <Box
                  key={tag}
                  className={`${styles.filterChip} ${styles[getTagClassName(tag)]}`}
                  component="span"
                >
                  {tag}
                  <IconButton
                    size="small"
                    onClick={() => handleDeleteTag(tag)}
                    sx={{ marginLeft: 0.5 }}
                  >
                    <CancelIcon fontSize="small" />
                  </IconButton>
                </Box>
              ))}
            </Box>
          )}
        </Box>

        <Box className={styles.contentContainer}>
          {filteredData.map((item, index) => (
            <Box
              key={index}
              className={styles.card}
              onClick={() => onItemClick(item.path)}
            >
              {renderCardContent(item, index)}
            </Box>
          ))}
        </Box>
      </Box>
    </Box>
  );
};

export default SolutionsPageLayout; 